import { Duration } from 'luxon';
import pluralize from 'pluralize';
import { useMemo, useState, VFC } from 'react';
import { Controller } from 'react-hook-form';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import DurationInput from 'dashboard/components-lib/DurationInput';
import { TimeIntervalInputUnit } from 'dashboard/components-lib/DurationInput/constants';
import { convertTimeToDesiredUnit } from 'dashboard/components-lib/DurationInput/utils';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { List, ListAttributes } from 'dashboard/models/list';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface UpdateListModalProps {
  handleClose: () => void;
  onSubmit: (list: List, attributes: ListAttributes) => Promise<void>;
  list: List;
}

const MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_YEARS = 3;

const UpdateListModal: VFC<UpdateListModalProps> = ({ list, handleClose, onSubmit }) => {
  const updatingFaceList = list.type === ApiModel.ListFace;
  interface UpdateListData {
    name: string;
    'items-expire-after-days': number;
    'retention-period-seconds'?: number;
  }

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useFormSecurely<UpdateListData>({
    defaultValues: {
      name: list.attributes.name,
      'items-expire-after-days': list.attributes['items-expire-after-days'],
    },
  });

  const submit = (data: UpdateListData) => {
    onSubmit(list, data);
  };

  const [retentionInputVisible, setRetentionInputVisible] = useState(
    !!list.attributes['retention-period-seconds'],
  );
  const [selectedArchiveTimeUnit, setSelectedArchiveTimeUnit] = useState<TimeIntervalInputUnit>(
    list.attributes['items-expire-after-days']
      ? TimeIntervalInputUnit.DAYS
      : TimeIntervalInputUnit.YEARS,
  );
  const [retentionTimeUnit, setRetentionTimeUnit] = useState<TimeIntervalInputUnit>(
    list.attributes['retention-period-seconds']
      ? TimeIntervalInputUnit.DAYS
      : TimeIntervalInputUnit.YEARS,
  );
  const [archiveTimeInput, setArchiveTimeInput] = useState<number | undefined>(
    list.attributes['items-expire-after-days']
      ? list.attributes['items-expire-after-days']
      : undefined,
  );
  const [retentionInput, setRetentionInput] = useState<number | undefined>(
    list.attributes['retention-period-seconds']
      ? convertTimeToDesiredUnit(
          list.attributes['retention-period-seconds'],
          TimeIntervalInputUnit.SECONDS,
          'days',
        )
      : undefined,
  );

  const retentionInputRules = useMemo(() => {
    return {
      required: {
        value: retentionInputVisible,
        message: 'Please set a retention period',
      },
      min: {
        value: 1,
        message: `Must not be less than 1 ${pluralize.singular(retentionTimeUnit)}`,
      },
    };
  }, [retentionInputVisible, retentionTimeUnit]);
  const archiveTimeInputRules = useMemo(() => {
    const maxYears = updatingFaceList ? MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_YEARS : 10;
    const retentionInputYears = convertTimeToDesiredUnit(
      retentionInput,
      retentionTimeUnit,
      'years',
    );
    const maxDuration =
      retentionInputYears && retentionInputYears < maxYears
        ? Duration.fromObject({ [retentionTimeUnit]: retentionInput })
        : Duration.fromObject({ years: maxYears });
    const maxDurationDisplay = maxDuration.as(selectedArchiveTimeUnit);
    return {
      required: {
        value: updatingFaceList,
        message: 'Please set an expiration',
      },
      max: {
        value: maxDuration.as('days'),
        message: `Must not be greater than ${maxDurationDisplay} ${selectedArchiveTimeUnit}`,
      },
      min: {
        value: 1,
        message: `Must not be less than 1 ${pluralize.singular(selectedArchiveTimeUnit)}`,
      },
    };
  }, [updatingFaceList, retentionInput, retentionTimeUnit, selectedArchiveTimeUnit]);

  return (
    <ModalContainer id='lists__update-list-modal'>
      <ModalHeader onClose={handleClose}>Update list</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit(submit)}>
          <FormGroup>
            <FormControlLabel htmlFor='update-list-modal__name'>Name</FormControlLabel>
            <Input
              id='update-list-modal__name'
              hasError={!!errors.name}
              {...register('name', {
                required: 'Please give your list a name',
                maxLength: { value: 255, message: 'Name must not exceed 255 characters' },
              })}
            />
            {errors.name && <FormError>{errors.name.message}</FormError>}
            <Spacer height={2} />
            {updatingFaceList && (
              <Controller
                control={control}
                name='items-expire-after-days'
                rules={archiveTimeInputRules}
                render={({ field: { onChange } }) => (
                  <>
                    <DurationInput
                      title='Deactivate items after'
                      subtitle={
                        updatingFaceList
                          ? 'Item state will change to Inactive and biometric data will be erased (max 3 years)'
                          : 'Item state will change to Inactive.'
                      }
                      hasError={!!errors['items-expire-after-days']}
                      onChange={(newValue) => {
                        setArchiveTimeInput(newValue.rawDuration);
                        setSelectedArchiveTimeUnit(newValue.unit);
                        onChange(newValue.convertedDuration);
                      }}
                      value={{ duration: archiveTimeInput, unit: selectedArchiveTimeUnit }}
                      desiredTimeUnit='days'
                    />
                    {errors['items-expire-after-days'] && (
                      <FormError>{errors['items-expire-after-days'].message}</FormError>
                    )}
                  </>
                )}
              />
            )}

            <Spacer height={2} />
            <Controller
              control={control}
              name='retention-period-seconds'
              rules={retentionInputRules}
              render={({ field: { onChange } }) => (
                <>
                  <DurationInput
                    title='Redact items after'
                    subtitle={
                      updatingFaceList
                        ? 'Item data will be erased'
                        : 'Item data will be erased and item state will change to Archived'
                    }
                    hasError={!!errors['retention-period-seconds']}
                    onChange={(newValue) => {
                      setRetentionInput(newValue.rawDuration);
                      setRetentionTimeUnit(newValue.unit);
                      onChange(newValue.convertedDuration);
                    }}
                    value={{ duration: retentionInput, unit: retentionTimeUnit }}
                    desiredTimeUnit='seconds'
                    onToggle={(toggleValue) => {
                      setRetentionInputVisible(toggleValue);
                      setRetentionInput(undefined);
                      setRetentionTimeUnit(TimeIntervalInputUnit.YEARS);
                      onChange(null);
                    }}
                    enableToggleByDefault={retentionInputVisible}
                  />
                  {retentionInputVisible && errors['retention-period-seconds'] && (
                    <FormError>{errors['retention-period-seconds'].message}</FormError>
                  )}
                </>
              )}
            />

            <Spacer height={2} />
          </FormGroup>
          <Button
            type='submit'
            width='auto'
            id='lists__update-list-modal--btn-update'
            isPending={isSubmitting}
            onClick={handleSubmit(submit)}
          >
            Update
          </Button>
        </Form>
      </ModalBody>
    </ModalContainer>
  );
};

export default UpdateListModal;
